import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navCategories } from '@/mocks/navigationData';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.category-dropdown-trigger') && !target.closest('.category-dropdown-menu')) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const navLinks = [
    { to: '/', label: '首页' },
    { to: '/category/sousuo-yinqing', label: '分类浏览' },
    { to: '/about', label: '关于我们' },
  ];

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50 border-b border-background-200'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg transition-transform duration-300 group-hover:scale-110">
              <i className="ri-compass-3-fill text-xl text-white"></i>
            </div>
            <span className="font-heading text-xl font-bold text-foreground-950">
              蓝导航
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  location.pathname === link.to
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}

            <div className="relative category-dropdown-trigger">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap ${
                  location.pathname.startsWith('/category')
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                分类导航
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isCategoryDropdownOpen && (
                <div className="category-dropdown-menu absolute top-full right-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200 overflow-hidden animate-[fadeIn_0.2s_ease-out]">
                  <div className="p-2">
                    {navCategories.map((cat) => (
                      <button
                        key={cat.slug}
                        onClick={() => {
                          navigate(`/category/${cat.slug}`);
                          setIsCategoryDropdownOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 text-left"
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                          <i className={`${cat.icon} text-base text-foreground-600`}></i>
                        </div>
                        <span className="font-medium whitespace-nowrap">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="/about"
              className="ml-3 px-5 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              提交网站
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors"
            aria-label="菜单"
          >
            <i className={`text-xl text-foreground-700 ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-[fadeIn_0.2s_ease-out]">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-2.5 rounded-lg text-sm font-medium ${
                  location.pathname === link.to
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-background-200">
              <p className="px-4 py-1 text-xs text-foreground-500 font-medium uppercase tracking-wider">分类导航</p>
              {navCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100"
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-100">
                    <i className={`${cat.icon} text-sm text-foreground-600`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
            <div className="pt-2">
              <a
                href="/about"
                className="block text-center px-4 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium"
              >
                提交网站
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}